import React, { useState, useEffect } from "react";
import { Table, Tooltip } from "antd";
import { IWorkflowSearchResult } from "../../../types/IWorkflowSearchResult";
import IconTextButton from "../../../components/iconTextButton/IconTextButton";
import { ComponentsHelper } from "../../../../core/services/ComponentHelper";
import { INTRAY, REPERTOIRE, SEARCH_VIEW_WORKFLOW } from "../../../Consts";
import { IRepertoireField } from "../../../types/IRepertoireField";
import { TableRowSelection } from "antd/lib/table/interface";
import { ILookupDictionary } from "../../../../lookup/types/ILookupDictionary";

interface IWorkflowSearchTableProps {
    tableContents: IWorkflowSearchResult[];
    searchResultsTableData: { fields: IRepertoireField[] };
    totalResults: number;
    selectAllWorkflow: (start: number, end: number, value: boolean) => void;
    selectWorkflow: (item: any, index: number) => void;
    currentPage: number;
    lookupValues: ILookupDictionary;
    resultsPerPage: number;
    updatePagination: (start: number, end: number, pageSize: number, page: number, section: string) => void;
    getUsageDetails?: (usageID: string, lookupValues?: ILookupDictionary) => void;
    getProductDetails?: (dataSource: string, productCoreID: number, isForWorkflowSession?: boolean, openNewTab?: boolean, id1?: string, id2?: string, type?: string, originOfRevision?: string, productionType?: string, musicDuration?: number[]) => void;
    getAgreementDetails?: (dataSource: string, agreementID: number) => void;
    getAdjustmentDetails?: (adjustmentId: string) => void;
    getWorkDetails?: (dataSource: string, workID: number) => void;
}

const WorkflowSearchTable: React.FC<IWorkflowSearchTableProps> = (props) => {
    const { tableContents, currentPage, resultsPerPage, updatePagination, selectWorkflow, selectAllWorkflow, totalResults, searchResultsTableData } = props;

    const [pageNumber, setPageNumber] = useState(currentPage || 1);
    const [pageSize, setPageSize] = useState(resultsPerPage || 10);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(() =>
        tableContents.filter((row) => row.selected).map((row) => row.ID)
    );

    useEffect(() => {
        setSelectedRowKeys(tableContents.filter((row) => row.selected).map((row) => row.ID));
    }, [tableContents]);

    const handlePageChange = (page: number, pageSize: number): void => {
        const start = (page - 1) * pageSize;
        const end = start + pageSize - 1;

        setPageNumber(page);
        setPageSize(pageSize);
        updatePagination(start, end, pageSize, page, SEARCH_VIEW_WORKFLOW);
    };

    const onClickExternalIcon = (record: IWorkflowSearchResult) => {
        const { getUsageDetails, getProductDetails, getAgreementDetails, getAdjustmentDetails, getWorkDetails, lookupValues } = props;

        const actionMap = {
            "Usage (Work Based)": () => getUsageDetails(record.entityID, lookupValues),
            "Usage (Product Based)": () => getUsageDetails(record.entityID),
            "Usage (Live Performance)": () => getUsageDetails(record.entityID),
            "Usage (Unidentified Performance)": () => getUsageDetails(record.entityID),
            "Intray Product": () => getProductDetails(record.setType, Number(record.entityID)),
            "Intray Agreement": () => getAgreementDetails(INTRAY, Number(record.entityID)),
            "Adjustment": () => getAdjustmentDetails(record.entityID),
            "Intray Work": () => getWorkDetails(INTRAY, Number(record.entityID)),
            "Repertoire Work": () => getWorkDetails(REPERTOIRE, Number(record.entityID)),
        };

        (actionMap[record.entityType])();
    };

    const getColumns = (): any[] => {
        const orderedFields = ComponentsHelper.orderFieldsBySpecificField(
            searchResultsTableData.fields.filter((field) => field.componentInstance === SEARCH_VIEW_WORKFLOW && field.name !== "noSearchResultsMessage"), "order"
        );
        return orderedFields.map((field) => {
            if (field.name === "selected" || field.name === "update_action") {
                return {
                    dataIndex: field.name,
                    title: '',
                    render: field.name === "update_action" ?
                        (_, record) => (
                            <span title={field.data}>
                                <IconTextButton
                                    icon="icon ms-Icon ms-Icon--OpenInNewTab"
                                    onClick={() => onClickExternalIcon(record)}
                                />
                            </span>
                        ) : undefined,
                    sorter: false,
                    width: 0,
                };
            }
            else if (field.name === "references") {
                return {
                    dataIndex: field.name,
                    title: field.data,
                    sorter: (a, b) => a[field.name]?.localeCompare(b[field.name]),
                    width: '25%',
                    ellipsis: { showTitle: false }, // disables the browser default title tooltip
                    render: (text: string) => (
                    <Tooltip title={text}>
                        <div
                        className="textOverFLowEllipsis"
                        >
                        {text}
                        </div>
                    </Tooltip>
                    ),
                };
            }
            else if (field.name === "values") {
                return {
                    dataIndex: field.name,
                    title: field.data,
                    sorter: false,
                };
            } else if (field.name === "entityName") {
                return {
                width: '25%',
                dataIndex: field.name,
                title: field.data,
                sorter: (a, b) => a[field.name]?.localeCompare(b[field.name]),
                                    render: (text: string) => (
                    <Tooltip title={text}>
                        <div
                        className="textOverFLowEllipsis"
                        >
                        {text}
                        </div>
                    </Tooltip>
                    ),
            }
        } else if (field.name === "update_action") {
            return {
                dataIndex: field.name,
                title: field.data,
                sorter: (a, b) => a[field.name]?.localeCompare(b[field.name]),
                width: 10,
            };

        }

            return {
                dataIndex: field.name,
                title: field.data,
                sorter: (a, b) => a[field.name]?.localeCompare(b[field.name]),
            };
        });
    };

    const rowSelection: TableRowSelection<IWorkflowSearchResult> = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
        onSelect: (record, selected) => selectWorkflow(record, tableContents.indexOf(record)),
        onSelectAll: (selected) => {
            const start = (pageNumber - 1) * pageSize;
            const end = start + pageSize;
            selectAllWorkflow(start, end, selected);
        },
    };

    return (
        <div className="col-md-12 flexColumn">
            <Table
                columns={getColumns()}
                dataSource={tableContents.map((item) => ({ key: item.ID, ...item }))}
                rowSelection={rowSelection}
                pagination={{
                    current: pageNumber,
                    pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 25, 50, 100, 250, 500, 1000],
                    position: ["bottomRight", "topRight"],
                    onChange: handlePageChange,
                    showTotal: (total) => (
                        <b>Result Set: {totalResults} &nbsp;&nbsp; Return Results: {total}</b>
                    ),
                }}
                size="small"
                className="parent-table workflow-table"
            />
        </div>
    );
};

export default WorkflowSearchTable;
